import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { lastListSearch } from "../incidents/list-view";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Check, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { notifyApiError, type ErrorMessages } from "../../lib/toast-error";
import { toApiFailure } from "../../lib/errors";
import { formatDate, formatDateTime } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  incidentKeys,
  useChangeStatus,
  useIncident,
  useTimeline,
  type IncidentResponse,
} from "../incidents/api";
import {
  isManualTimelineType,
  TIMELINE_TYPE_LABEL_KEY,
  timelineText,
} from "../incidents/status";
import { StatusBadge } from "../incidents/components/status-badge";
import {
  activeUsers,
  toUserMap,
  userName,
  useUsers,
  type UserSummary,
} from "../users/api";
import {
  postmortemKeys,
  useChangeFollowupTaskStatus,
  useCreateFollowupTask,
  usePostmortem,
  useUpdateFollowupTask,
  useUpsertPostmortem,
  type FollowupStatus,
  type FollowupTaskResponse,
  type PostmortemResponse,
  type UpsertPostmortemRequest,
} from "./api";
import {
  FOLLOWUP_STATUS_LABEL_KEY,
  FOLLOWUP_STATUSES,
  MANDATORY_SECTIONS,
  missingMandatorySections,
  OPTIONAL_SECTIONS,
  POSTMORTEM_PROBLEM_KEY,
  SECTION_LABEL_KEY,
} from "./completeness";
import { canChangeTaskStatus, canEditFollowups, canEditPostmortem } from "./permissions";
import { isIncidentParty } from "../../lib/access";
import { isDraft } from "./completeness";
import { useAuth } from "../../lib/auth";
import { useInputDraft } from "../drafts/use-input-draft";
import { DraftRestoreBanner, DraftSaveStatusLine } from "../drafts/draft-banner";
import type { DraftPayload } from "../drafts/draft-buffer";
import { readString, samePayload, toDraftPayload } from "../drafts/payload";

/** Explicit form value shape (mirrors UpsertPostmortemRequest for this slice). */
interface PostmortemFormValues {
  cause: string;
  affectedComponents: string;
  resolution: string;
  prevention: string;
  positiveLearnings: string;
  earlierDetectable: string;
}

type NotifyError = (error: unknown, onConflict?: () => void) => void;

/** The mask's fields as [NF-022] keeps them. */
const DRAFT_FIELDS = [
  "cause",
  "affectedComponents",
  "resolution",
  "prevention",
  "positiveLearnings",
  "earlierDetectable",
] as const satisfies readonly (keyof PostmortemFormValues)[];

/** A stored draft back into the mask; whatever it lacks keeps the current value. */
function fromDraft(payload: DraftPayload, current: PostmortemFormValues): PostmortemFormValues {
  return {
    cause: readString(payload, "cause", current.cause),
    affectedComponents: readString(payload, "affectedComponents", current.affectedComponents),
    resolution: readString(payload, "resolution", current.resolution),
    prevention: readString(payload, "prevention", current.prevention),
    positiveLearnings: readString(payload, "positiveLearnings", current.positiveLearnings),
    earlierDetectable: readString(payload, "earlierDetectable", current.earlierDetectable),
  };
}

/**
 * Error notifier that first maps the postmortem-specific RFC-7807 problem
 * titles (error.postmortem_*) to SPA catalog messages, then falls back to the
 * shared status-code handling (412 refetch etc.).
 */
function usePostmortemNotifier(): NotifyError {
  const { t } = useTranslation();
  const errorMessages: ErrorMessages = {
    precondition: t("errors.precondition"),
    forbidden: t("errors.forbidden"),
    conflict: t("errors.conflict"),
    generic: t("errors.saveFailed"),
  };
  return (error: unknown, onConflict?: () => void): void => {
    const failure = toApiFailure(error);
    const mappedKey = failure.problem
      ? POSTMORTEM_PROBLEM_KEY[failure.problem.title]
      : undefined;
    if (mappedKey) {
      toast.error(t(mappedKey));
      return;
    }
    notifyApiError(error, errorMessages, onConflict);
  };
}

export function PostmortemPage(): JSX.Element {
  const { id } = useParams({ from: "/incidents/$id/postmortem" });
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Full-page success after a successful close (M-007), replacing toast+redirect.
  const [closed, setClosed] = useState(false);

  const incidentQuery = useIncident(id);
  const postmortemQuery = usePostmortem(id);
  const timelineQuery = useTimeline(id);
  const usersQuery = useUsers();
  const userById = useMemo(() => toUserMap(usersQuery.data), [usersQuery.data]);

  if (closed) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
            <CheckCircle2 aria-hidden="true" className="h-12 w-12 text-success" />
            <h2 className="text-xl font-semibold">{t("postmortem.closedTitle")}</h2>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Button
                onClick={() =>
                  void navigate({ to: "/incidents/$id", params: { id } })
                }
              >
                {t("postmortem.closedToIncident")}
              </Button>
              <Button
                variant="outline"
                onClick={() => void navigate({ to: "/incidents", search: lastListSearch() })}
              >
                {t("postmortem.closedToList")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (incidentQuery.isLoading || postmortemQuery.isLoading) {
    return <PageSkeleton />;
  }

  if (
    incidentQuery.isError ||
    !incidentQuery.data ||
    postmortemQuery.isError ||
    postmortemQuery.data === undefined
  ) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
      >
        <span>{t("errors.load")}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void incidentQuery.refetch();
            void postmortemQuery.refetch();
          }}
        >
          {t("common.retry")}
        </Button>
      </div>
    );
  }

  const incident = incidentQuery.data;
  const postmortem = postmortemQuery.data;

  // Write mode follows the server (display-only, server re-checks): the
  // incident's `can` ([F-027]) AND create only out of geloest/beobachtet
  // (US-006) resp. update in every status except abgeschlossen (DO-004).
  const statusAllowsWrite = postmortem
    ? incident.status !== "abgeschlossen"
    : incident.status === "geloest" || incident.status === "beobachtet";
  const writable = canEditPostmortem(incident) && statusAllowsWrite;
  const tasksWritable = canEditFollowups(incident) && statusAllowsWrite;

  return (
    <div className="space-y-6">
      <Link
        to="/incidents/$id"
        params={{ id }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("postmortem.backToIncident")}
      </Link>

      {/* Kontext-Kopf (M-007): eyebrow, the incident TITLE as the h1, then the
          status/owner line plus the resolved date when the payload carries it. */}
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("postmortem.eyebrow", { number: incident.number })}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {incident.title}
          </h1>
          <StatusBadge status={incident.status} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>
            {t("postmortem.owner")}:{" "}
            {incident.ownerId
              ? userName(incident.ownerId, userById)
              : t("postmortem.notAssigned")}
          </span>
          {incident.closedAt ? (
            <span>
              {t("postmortem.resolvedOn", { date: formatDate(incident.closedAt) })}
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          {writable ? (
            <PostmortemEditor
              incident={incident}
              postmortem={postmortem}
              onClosed={() => setClosed(true)}
            />
          ) : (
            <PostmortemReadOnly postmortem={postmortem} />
          )}

          <FollowupTasksCard
            incidentId={incident.id}
            postmortem={postmortem}
            writable={tasksWritable}
            users={activeUsers(usersQuery.data)}
            userById={userById}
          />
        </div>

        <aside>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("detail.timeline")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {timelineQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">
                  {t("common.loading")}
                </p>
              ) : (timelineQuery.data?.items ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("detail.timelineEmpty")}
                </p>
              ) : (
                <ol className="space-y-3">
                  {(timelineQuery.data?.items ?? []).map((event) => (
                    <li
                      key={event.id}
                      className="rounded-md border p-2.5 text-xs"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Badge
                          variant={
                            isManualTimelineType(event.type)
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {t(TIMELINE_TYPE_LABEL_KEY[event.type])}
                        </Badge>
                        <span className="text-muted-foreground">
                          {formatDateTime(event.createdAt)}
                        </span>
                      </div>
                      <div className="mt-1 text-muted-foreground">
                        {t("detail.by")} {userName(event.authorId, userById)}
                      </div>
                      {event.text ? (
                        <p className="mt-1.5 whitespace-pre-wrap">
                          {timelineText(event, t)}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function PostmortemEditor({
  incident,
  postmortem,
  onClosed,
}: {
  incident: IncidentResponse;
  postmortem: PostmortemResponse | null;
  onClosed: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Draft-saved inline confirmation (M-007) and the inline save/close error
  // banner that replaces the toast for these two actions.
  const [draftSaved, setDraftSaved] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const upsert = useUpsertPostmortem(incident.id);
  const changeStatus = useChangeStatus(incident.id);

  const schema = useMemo(
    () =>
      z.object({
        cause: z
          .string()
          .trim()
          .min(1, t("postmortem.validation.causeRequired")),
        affectedComponents: z
          .string()
          .trim()
          .min(1, t("postmortem.validation.affectedComponentsRequired")),
        resolution: z
          .string()
          .trim()
          .min(1, t("postmortem.validation.resolutionRequired")),
        prevention: z
          .string()
          .trim()
          .min(1, t("postmortem.validation.preventionRequired")),
        positiveLearnings: z.string(),
        earlierDetectable: z.string(),
      }),
    [t],
  );

  const form = useForm<PostmortemFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      cause: postmortem?.cause ?? "",
      affectedComponents: postmortem?.affectedComponents ?? "",
      resolution: postmortem?.resolution ?? "",
      prevention: postmortem?.prevention ?? "",
      positiveLearnings: postmortem?.positiveLearnings ?? "",
      earlierDetectable: postmortem?.earlierDetectable ?? "",
    },
  });

  // [NF-022] / [AC-030]: what is typed here is saved without being asked,
  // apart from the F-007 Entwurf a user saves on purpose; an unsaved state
  // found on arrival is offered back. Only a difference from the stored
  // postmortem is worth keeping.
  const { user } = useAuth();
  const draft = useInputDraft({ form: "postmortem", context: incident.id, userId: user?.userId });
  const scheduleDraft = draft.schedule;
  const clearDraft = draft.clear;
  const baseline = useRef<DraftPayload>(toDraftPayload({ ...form.getValues() }, DRAFT_FIELDS));
  useEffect(() => {
    const subscription = form.watch((values) => {
      const payload = toDraftPayload({ ...values }, DRAFT_FIELDS);
      scheduleDraft(samePayload(payload, baseline.current) ? null : payload);
    });
    return () => subscription.unsubscribe();
  }, [form, scheduleDraft]);

  const restoreDraft = (): void => {
    const payload = draft.restore();
    if (payload) form.reset(fromDraft(payload, form.getValues()));
  };

  // A deliberate save replaces the autosaved state (the server drops it in the
  // same transaction). Whatever was typed while the request ran is newer than
  // the save, and goes back to the draft store.
  const afterSave = (saved: PostmortemFormValues): void => {
    baseline.current = toDraftPayload({ ...saved }, DRAFT_FIELDS);
    void clearDraft().then(() => {
      const now = toDraftPayload({ ...form.getValues() }, DRAFT_FIELDS);
      if (!samePayload(now, baseline.current)) scheduleDraft(now);
    });
  };

  // Live close-gate: which mandatory sections are still empty right now.
  const watched = form.watch();
  const missing = missingMandatorySections(watched);

  const onConflict = (): void => {
    void queryClient.invalidateQueries({
      queryKey: incidentKeys.detail(incident.id),
    });
    void queryClient.invalidateQueries({
      queryKey: postmortemKeys.byIncident(incident.id),
    });
  };

  // Resolve a save/close failure to an inline German message: the
  // postmortem-specific RFC-7807 titles first, then the server detail, then the
  // generic fallback. A 412 also triggers the conflict refetch (DE-119).
  const handleActionError = (error: unknown): void => {
    const failure = toApiFailure(error);
    if (failure.status === 412) onConflict();
    const mappedKey = failure.problem
      ? POSTMORTEM_PROBLEM_KEY[failure.problem.title]
      : undefined;
    setActionError(
      mappedKey ? t(mappedKey) : failure.problem?.detail ?? t("errors.saveFailed"),
    );
  };

  const toBody = (
    values: PostmortemFormValues,
    state: "entwurf" | "final",
  ): UpsertPostmortemRequest => {
    const optional = (value: string): string | null => {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    };
    return {
      cause: values.cause.trim(),
      affectedComponents: values.affectedComponents.trim(),
      resolution: values.resolution.trim(),
      prevention: values.prevention.trim(),
      positiveLearnings: optional(values.positiveLearnings),
      earlierDetectable: optional(values.earlierDetectable),
      state,
    };
  };

  // F-007: a draft save runs NO completeness validation. This deliberately
  // bypasses form.handleSubmit, which gates on the four mandatory sections -
  // that gate was the defect: it made "Entwurf speichern" require exactly what
  // a draft is allowed to be missing, so a released Must had no implementation
  // on any surface. The server agrees; it only checks on the transition to
  // final.
  const onSaveDraft = () => {
    setActionError(null);
    const values = form.getValues();
    upsert.mutate(toBody(values, "entwurf"), {
      onSuccess: () => {
        afterSave(values);
        toast.success(t("postmortem.saved"));
        setDraftSaved(true);
        window.setTimeout(() => setDraftSaved(false), 4000);
      },
      onError: (error) => handleActionError(error),
    });
  };

  // Finalising IS the checked transition (F-007), so this one keeps
  // handleSubmit and its per-field messages.
  const onSaveFinal = form.handleSubmit((values) => {
    setActionError(null);
    upsert.mutate(toBody(values, "final"), {
      onSuccess: () => {
        afterSave(values);
        toast.success(t("postmortem.finalised"));
      },
      onError: (error) => handleActionError(error),
    });
  });

  // Close (GR-001 geloest -> abgeschlossen). Since LP-030 (RYD-PA,
  // 2026-09-18) the Postmortem is no precondition for closing: what is on the
  // page is saved - final when every mandatory section is filled, otherwise as
  // a draft, which stays a draft - and then the status flips with the If-Match
  // precondition.
  const onClose = async (): Promise<void> => {
    setActionError(null);
    const complete = missing.length === 0;
    const values = form.getValues();
    try {
      await upsert.mutateAsync(toBody(values, complete ? "final" : "entwurf"));
      afterSave(values);
      await changeStatus.mutateAsync({
        toStatus: "abgeschlossen",
        version: incident.version,
      });
      onClosed();
    } catch (error) {
      handleActionError(error);
    }
  };

  const pending = upsert.isPending || changeStatus.isPending;
  const showClose = incident.status === "geloest";
  const closesAsDraft = missing.length > 0;
  const filledCount = MANDATORY_SECTIONS.length - missing.length;

  return (
    <Form {...form}>
      <form onSubmit={(event) => event.preventDefault()} className="space-y-6">
        {draft.offered ? (
          <DraftRestoreBanner
            mask="postmortem"
            offered={draft.offered}
            onRestore={restoreDraft}
            onDiscard={draft.dismiss}
          />
        ) : null}
        {/* AC-008: a draft MUST be visibly marked as incomplete. */}
        {isDraft(postmortem) ? (
          <div className="rounded-md border border-amber-500/50 bg-amber-50 px-4 py-3 text-sm dark:bg-amber-950/30">
            <p className="font-medium">{t("postmortem.draftBadge")}</p>
            <p className="text-muted-foreground">{t("postmortem.draftHint")}</p>
          </div>
        ) : null}
        <Card>
          <CardContent className="space-y-6 pt-6">
            <p className="text-sm text-muted-foreground">
              {t("postmortem.editorIntro")}
            </p>
            {/* Mandatory-section header with a live "n von 4 befüllt" chip. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">
                {t("postmortem.mandatoryHeader")}
              </p>
              <Badge variant="secondary">
                {t("postmortem.filledCounter", {
                  filled: filledCount,
                  total: MANDATORY_SECTIONS.length,
                })}
              </Badge>
            </div>
            {MANDATORY_SECTIONS.map((section, index) => {
              const filled = (watched[section] ?? "").trim().length > 0;
              return (
                <FormField
                  key={section}
                  control={form.control}
                  name={section}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <span>
                          {t(SECTION_LABEL_KEY[section])} <RequiredMark />
                        </span>
                        {filled ? (
                          <span className="inline-flex items-center gap-1 text-xs font-normal text-success">
                            <Check aria-hidden="true" className="h-3.5 w-3.5" />
                            {t("postmortem.filled")}
                          </span>
                        ) : null}
                      </FormLabel>
                      <FormControl>
                        <Textarea rows={5} autoFocus={index === 0} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("postmortem.lessonsTitle")}
            </CardTitle>
            <Badge variant="outline">{t("postmortem.lessonsOptionalBadge")}</Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            {OPTIONAL_SECTIONS.map((section) => (
              <FormField
                key={section}
                control={form.control}
                name={section}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(SECTION_LABEL_KEY[section])} {t("postmortem.optionalLabel")}
                    </FormLabel>
                    <FormControl>
                      <Textarea rows={4} {...field} />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {t("postmortem.optionalHint")}
                    </p>
                  </FormItem>
                )}
              />
            ))}
          </CardContent>
        </Card>

        <div className="space-y-3">
          {/* Save/close error (M-007): inline destructive banner above footer. */}
          {actionError ? (
            <div
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
            >
              {actionError}
            </div>
          ) : null}

          {/* Information, not a gate (LP-030): closing with sections still
              missing leaves the Postmortem as a draft. */}
          {showClose && closesAsDraft ? (
            <div
              data-testid="postmortem_closes-as-draft"
              className="rounded-lg border bg-muted/50 p-3 text-sm text-muted-foreground"
            >
              {t("postmortem.missingSectionsCallout", {
                sections: missing
                  .map((section) => t(SECTION_LABEL_KEY[section]))
                  .join(", "),
              })}
            </div>
          ) : null}

          <div className="flex flex-col items-end gap-2">
            <div className="flex w-full flex-wrap items-center justify-end gap-3">
              <div className="mr-auto">
                <DraftSaveStatusLine mask="postmortem" status={draft.status} savedAt={draft.savedAt} />
              </div>
              {/* Inline draft-saved note (M-007), ~4s after a successful save. */}
              {draftSaved ? (
                <span className="text-sm text-muted-foreground">
                  {t("postmortem.draftSavedInline")}
                </span>
              ) : null}
              <Button
                type="button"
                variant="outline"
                onClick={() => onSaveDraft()}
                disabled={pending}
              >
                {pending ? t("postmortem.saving") : t("postmortem.saveDraft")}
              </Button>
              {/* F-007: finalising is its own act and the only point the
                  mandatory sections are checked. Hidden once the postmortem
                  is already final. */}
              {postmortem?.state !== "final" ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void onSaveFinal()}
                  disabled={pending}
                >
                  {t("postmortem.saveFinal")}
                </Button>
              ) : null}
              {showClose ? (
                <Button
                  type="button"
                  onClick={() => void onClose()}
                  disabled={pending}
                >
                  {changeStatus.isPending
                    ? t("postmortem.closing")
                    : t("postmortem.close")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}

/** Red asterisk marking a mandatory postmortem section (M-007); decorative. */
function RequiredMark(): JSX.Element {
  return (
    <span aria-hidden="true" className="text-destructive-text">
      *
    </span>
  );
}

function PostmortemReadOnly({
  postmortem,
}: {
  postmortem: PostmortemResponse | null;
}): JSX.Element {
  const { t } = useTranslation();

  if (!postmortem) {
    return (
      <div className="rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground">
        {t("postmortem.none")}
      </div>
    );
  }

  const optionalWithContent = OPTIONAL_SECTIONS.filter((section) =>
    (postmortem[section] ?? "").trim(),
  );

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {t("postmortem.readOnlyHint")}
      </p>
      <Card>
        <CardContent className="space-y-5 pt-6">
          {MANDATORY_SECTIONS.map((section) => (
            <div key={section} className="space-y-1">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t(SECTION_LABEL_KEY[section])}
              </div>
              <p className="whitespace-pre-wrap text-sm">
                {postmortem[section].trim() || t("common.none")}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
      {optionalWithContent.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("postmortem.lessonsTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {optionalWithContent.map((section) => (
              <div key={section} className="space-y-1">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t(SECTION_LABEL_KEY[section])}
                </div>
                <p className="whitespace-pre-wrap text-sm">
                  {postmortem[section]}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function FollowupTasksCard({
  incidentId,
  postmortem,
  writable,
  users,
  userById,
}: {
  incidentId: string;
  postmortem: PostmortemResponse | null;
  writable: boolean;
  users: UserSummary[];
  userById: Map<string, UserSummary>;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {t("postmortem.tasks.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!postmortem ? (
          <p className="text-sm text-muted-foreground">
            {writable
              ? t("postmortem.tasks.needsPostmortem")
              : t("postmortem.tasks.empty")}
          </p>
        ) : (
          <>
            {postmortem.followupTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("postmortem.tasks.empty")}
              </p>
            ) : (
              <ul className="space-y-3">
                {postmortem.followupTasks.map((task) => (
                  <FollowupTaskRow
                    key={task.id}
                    incidentId={incidentId}
                    task={task}
                    writable={writable}
                    users={users}
                    userById={userById}
                  />
                ))}
              </ul>
            )}
            {writable ? (
              <NewFollowupTaskRow incidentId={incidentId} users={users} />
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FollowupTaskRow({
  incidentId,
  task,
  writable,
  users,
  userById,
}: {
  incidentId: string;
  task: FollowupTaskResponse;
  writable: boolean;
  users: UserSummary[];
  userById: Map<string, UserSummary>;
}): JSX.Element {
  const { t } = useTranslation();
  const notifyError = usePostmortemNotifier();
  const updateTask = useUpdateFollowupTask(incidentId);
  const changeTaskStatus = useChangeFollowupTaskStatus(incidentId);

  // AC-009: the status select opens where the task's own `can` holds
  // `followup.status` - its assignee or the incident's Owner, with the role
  // for it; the inline FIELD editor below follows `writable`. Not gated on
  // incident status - see canChangeTaskStatus.
  const statusEditable = canChangeTaskStatus(task);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [assignedToId, setAssignedToId] = useState(task.assignedToId);
  const [dueDate, setDueDate] = useState(task.dueDate.slice(0, 10));

  const startEdit = (): void => {
    setTitle(task.title);
    setDescription(task.description ?? "");
    setAssignedToId(task.assignedToId);
    setDueDate(task.dueDate.slice(0, 10));
    setEditing(true);
  };

  const onSave = (): void => {
    updateTask.mutate(
      {
        taskId: task.id,
        body: {
          title: title.trim(),
          description: description.trim() ? description.trim() : null,
          assignedToId,
          dueDate,
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success(t("postmortem.tasks.saved"));
        },
        onError: (error) => notifyError(error),
      },
    );
  };

  const onChangeStatus = (value: string): void => {
    changeTaskStatus.mutate(
      { taskId: task.id, status: value as FollowupStatus },
      {
        onSuccess: () => toast.success(t("postmortem.tasks.statusChanged")),
        onError: (error) => notifyError(error),
      },
    );
  };

  if (editing) {
    return (
      <li className="space-y-3 rounded-md border p-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-xs font-medium">
              {t("postmortem.tasks.fieldTitle")}
            </label>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <label className="text-xs font-medium">
              {t("postmortem.tasks.fieldDescription")}
            </label>
            <Textarea
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">
              {t("postmortem.tasks.fieldAssignee")}
            </label>
            <Select value={assignedToId} onValueChange={setAssignedToId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem
                    key={user.id}
                    value={user.id}
                    disabled={!isIncidentParty(user) && user.id !== task.assignedToId}
                  >
                    {isIncidentParty(user) || user.id === task.assignedToId
                      ? user.displayName
                      : t("incidentParty.notEligible", { name: user.displayName })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium">
              {t("postmortem.tasks.fieldDueDate")}
            </label>
            <Input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(false)}
            disabled={updateTask.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={
              updateTask.isPending ||
              title.trim().length === 0 ||
              dueDate.length === 0
            }
          >
            {updateTask.isPending
              ? t("common.loading")
              : t("postmortem.tasks.save")}
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
      <div className="min-w-0 space-y-0.5">
        <p className="text-sm font-medium">{task.title}</p>
        {task.description ? (
          <p className="whitespace-pre-wrap text-xs text-muted-foreground">
            {task.description}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {userName(task.assignedToId, userById)} -{" "}
          {t("postmortem.tasks.dueOn")} {formatDate(task.dueDate)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {statusEditable ? (
          <Select
            value={task.status}
            onValueChange={onChangeStatus}
            disabled={changeTaskStatus.isPending}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FOLLOWUP_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(FOLLOWUP_STATUS_LABEL_KEY[status])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="secondary">
            {t(FOLLOWUP_STATUS_LABEL_KEY[task.status])}
          </Badge>
        )}
        {writable ? (
          <Button variant="ghost" size="sm" onClick={startEdit}>
            {t("postmortem.tasks.edit")}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function NewFollowupTaskRow({
  incidentId,
  users,
}: {
  incidentId: string;
  users: UserSummary[];
}): JSX.Element {
  const { t } = useTranslation();
  const notifyError = usePostmortemNotifier();
  const createTask = useCreateFollowupTask(incidentId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  const [dueDate, setDueDate] = useState("");

  const complete =
    title.trim().length > 0 && assignedToId.length > 0 && dueDate.length > 0;

  const onAdd = (): void => {
    createTask.mutate(
      {
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        assignedToId,
        dueDate,
      },
      {
        onSuccess: () => {
          setTitle("");
          setDescription("");
          setAssignedToId("");
          setDueDate("");
          toast.success(t("postmortem.tasks.added"));
        },
        onError: (error) => notifyError(error),
      },
    );
  };

  return (
    <div className="space-y-3 border-t pt-4">
      <p className="text-sm font-medium">{t("postmortem.tasks.add")}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs font-medium">
            {t("postmortem.tasks.fieldTitle")}
          </label>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-xs font-medium">
            {t("postmortem.tasks.fieldDescription")}
          </label>
          <Textarea
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium">
            {t("postmortem.tasks.fieldAssignee")}
          </label>
          <Select value={assignedToId} onValueChange={setAssignedToId}>
            <SelectTrigger>
              <SelectValue
                placeholder={t("postmortem.tasks.selectPlaceholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id} disabled={!isIncidentParty(user)}>
                  {isIncidentParty(user)
                    ? user.displayName
                    : t("incidentParty.notEligible", { name: user.displayName })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium">
            {t("postmortem.tasks.fieldDueDate")}
          </label>
          <Input
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={onAdd}
          disabled={!complete || createTask.isPending}
        >
          {createTask.isPending
            ? t("postmortem.tasks.adding")
            : t("postmortem.tasks.add")}
        </Button>
      </div>
    </div>
  );
}

/** Structured postmortem skeleton (K6): a context head, the editor column with
 *  stacked section shapes and the side timeline panel. */
function PageSkeleton(): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      {/* Context head: eyebrow, incident title, status/owner line. */}
      <div className="space-y-2">
        <div className="h-3 w-40 animate-pulse rounded bg-muted" />
        <div className="h-7 w-2/3 animate-pulse rounded bg-muted" />
        <div className="h-4 w-48 animate-pulse rounded bg-muted" />
      </div>
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        {/* Editor column: mandatory-section header + four textarea shapes. */}
        <div className="space-y-4 rounded-xl border p-6">
          <div className="h-4 w-40 animate-pulse rounded bg-muted" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <div className="h-3 w-32 animate-pulse rounded bg-muted" />
              <div className="h-20 w-full animate-pulse rounded-md bg-muted" />
            </div>
          ))}
        </div>
        {/* Side panel: the timeline card shape. */}
        <div className="h-72 w-full animate-pulse rounded-xl bg-muted" />
      </div>
    </div>
  );
}
